import { useState, useEffect } from 'react';
import config from '../../config';
import axios from 'axios';
import history from '../../utils/browserHistory';
import { customerPortalEnabled } from '../../utils/customerPortal';
import icons from '../../icons';
import { requestErrorMessage } from '../../utils/requestErrorMessage';
import '../preview/dark-utilities.css';

const PASSWORD_RULES = [
  { id: 'length', label: '8-16 characters', test: value => value.length >= 8 && value.length <= 16 },
  { id: 'lower', label: 'At least 1 lowercase Latin letter', test: value => /[a-z]/.test(value) },
  { id: 'upper', label: 'At least 1 uppercase Latin letter', test: value => /[A-Z]/.test(value) },
  { id: 'symbol', label: 'At least 1 special character: @$!%*?&', test: value => /[@$!%*?&]/.test(value) }
];

const RequirementItem = ({ passed, label }) => (
  <li className='flex items-start gap-2 text-left text-[13px] leading-5'>
    <span
      className={`mt-[7px] h-2 w-2 shrink-0 rounded-full ${
        passed ? 'bg-coms-ok' : 'bg-coms-line-strong'
      }`}
    />
    <span className={passed ? 'text-coms-ok' : 'text-coms-line-strong'}>{label}</span>
  </li>
);

const PasswordField = ({ id, label, value, onChange, showValue, onToggleVisibility, placeholder }) => (
  <>
    <label htmlFor={id} className='mb-1.5 text-[13px] font-medium text-coms-ink'>
      {label}
    </label>
    <div className='relative mb-4'>
      <input
        id={id}
        type={showValue ? 'text' : 'password'}
        placeholder={placeholder}
        className='h-11 w-full rounded-lg border border-coms-line px-3 pr-11 text-[14px] text-coms-ink outline-none transition-colors placeholder:text-coms-ink-3 focus:border-coms-accent focus:ring-2 focus:ring-coms-bad-soft'
        value={value}
        onChange={onChange}
        autoComplete='new-password'
        required
      />
      <button
        type='button'
        onClick={onToggleVisibility}
        className='absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-coms-ink-3 hover:bg-coms-surface-2 hover:text-coms-ink-2'
        aria-label={showValue ? 'Hide password' : 'Show password'}
      >
        {showValue ? (
          <icons.hiddenPasswordIcon style={{ width: '18px', height: '18px' }} />
        ) : (
          <icons.visiblePasswordIcon style={{ width: '18px', height: '18px' }} />
        )}
      </button>
    </div>
  </>
);

const TalentResetPasswordForm = ({
  userType = 'auto',
  portalLabel = 'COMS',
  loginPath = '/login'
}) => {
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Extract token from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setToken(params.get('token') || '');
  }, []);

  const allRulesPassed = PASSWORD_RULES.every(rule => rule.test(newPassword));
  const passwordsMatch = Boolean(newPassword && confirmPassword && newPassword === confirmPassword);
  const canSubmit = allRulesPassed && passwordsMatch && !isSubmitting;

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    if (!allRulesPassed) {
      setError('Please make sure the password meets all requirements.');
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match.');
      return;
    }

    try {
      setIsSubmitting(true);
      await axios.post(`${config.API_URL}${userType==='customer'?'/customer-auth/reset':'/reset-password'}`, { token, newPassword, type: userType });
      history.push(loginPath);
    } catch (err) {
      const message = requestErrorMessage(err, 'Something went wrong. Please try again.');
      setError(message.endsWith('.') ? message : `${message}.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return (
      <main className='min-h-screen bg-coms-surface-2 px-4 py-10 text-left'>
        <section className='mx-auto w-full max-w-[520px] overflow-hidden rounded-[20px] border border-coms-surface-2 bg-coms-surface shadow-[0_24px_60px_rgba(15,23,42,0.10)]'>
          <div className='h-1.5 bg-coms-accent' />
          <div className='p-8'>
            <p className='mb-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-coms-accent'>
              Reset link problem
            </p>
            <h1 className='mb-3 text-[28px] font-semibold text-coms-ink'>Invalid or missing token</h1>
            <p className='text-[14px] leading-6 text-coms-ink-2'>
              Please open the latest password reset email, or request a new reset link from the login page.
            </p>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className='min-h-screen bg-coms-surface-2 px-4 py-8 text-left sm:py-12'>
      <section className='mx-auto grid w-full max-w-[920px] overflow-hidden rounded-[22px] border border-coms-surface-2 bg-coms-surface shadow-[0_24px_60px_rgba(15,23,42,0.10)] md:grid-cols-[1fr_0.82fr]'>
        <div className='relative bg-coms-ink p-8 text-white sm:p-10'>
          <div className='absolute inset-x-0 top-0 h-1.5 bg-coms-accent' />
          <p className='mb-3 text-[12px] font-semibold uppercase tracking-[0.14em] text-coms-bad-soft'>
            {portalLabel}
          </p>
          <h1 className='mb-4 text-[32px] font-semibold leading-tight'>Create a new password</h1>
          <p className='max-w-[360px] text-[14px] leading-6 text-coms-line-strong'>
            Your password must match the rules below. When every requirement turns green, you can save it and sign in.
          </p>

          <div className='mt-8 rounded-xl border border-white/10 bg-coms-surface/[0.08] p-5'>
            <p className='mb-3 text-[13px] font-semibold text-white'>Password requirements</p>
            <ul className='space-y-2'>
              {PASSWORD_RULES.map(rule => (
                <RequirementItem
                  key={rule.id}
                  passed={newPassword ? rule.test(newPassword) : false}
                  label={rule.label}
                />
              ))}
              <RequirementItem passed={passwordsMatch} label='Confirmation password must match' />
            </ul>
          </div>
        </div>

        <form onSubmit={handleSubmit} className='flex flex-col justify-center p-8 sm:p-10'>
          <div className='mb-6'>
            <h2 className='mb-2 text-[24px] font-semibold text-coms-ink'>Reset Password</h2>
            <p className='text-[13px] leading-5 text-coms-ink-3'>
              Use Latin letters and one of these special characters: @$!%*?&
            </p>
          </div>

          <PasswordField
            id='newPassword'
            label='New password'
            value={newPassword}
            onChange={e => {
              setNewPassword(e.target.value);
              setError('');
            }}
            showValue={showNewPassword}
            onToggleVisibility={() => setShowNewPassword(prev => !prev)}
            placeholder='Enter new password'
          />

          <PasswordField
            id='confirmPassword'
            label='Confirm password'
            value={confirmPassword}
            onChange={e => {
              setConfirmPassword(e.target.value);
              setError('');
            }}
            showValue={showConfirmPassword}
            onToggleVisibility={() => setShowConfirmPassword(prev => !prev)}
            placeholder='Repeat new password'
          />

          {error && (
            <div className='mb-4 rounded-lg border border-coms-bad-soft bg-coms-bad-soft px-3 py-2 text-[13px] leading-5 text-coms-accent'>
              {error}
            </div>
          )}

          <button
            type='submit'
            disabled={!canSubmit}
            className='h-11 w-full rounded-lg bg-coms-accent px-4 text-[14px] font-semibold text-white transition-colors hover:bg-coms-accent disabled:cursor-not-allowed disabled:bg-coms-line-strong'
          >
            {isSubmitting ? 'Changing password...' : 'Change Password'}
          </button>
        </form>
      </section>
    </main>
  );
};

// Customer-type resets belong to the disabled customer portal: never render the form or call /customer-auth/*.
const TalentResetPassword = props => {
  const blocked = props.userType === 'customer' && !customerPortalEnabled();
  useEffect(() => { if (blocked) history.push(props.loginPath || '/login'); }, [blocked, props.loginPath]);
  return blocked ? null : <TalentResetPasswordForm {...props} />;
};

export default TalentResetPassword;
